export interface StarSelectorOptions {
  element: HTMLElement
  rateCallback?: (rating: number, done: () => void) => void
  onHover?: (currentRating: number, rating: number | null) => void
  onLeave?: (currentRating: number, rating: number | null) => void
  max?: number
  rating?: number
  starSize?: number
  step?: number
  showToolTip?: boolean
  reverse?: boolean
  readOnly?: boolean
  isBusyText?: string
  disableText?: string
  ratingText?: string
}

export class StarSelector {
  private static readonly DEFAULT_MAX_STARS = 5
  private static readonly DEFAULT_STAR_SIZE = 16
  private static readonly DEFAULT_STEP = 1
  private static readonly PERCENT_MAX = 100
  private static readonly RATING_PLACEHOLDER = "{rating}/{maxRating}"

  private readonly element: HTMLElement
  private readonly starValueElement: HTMLElement
  private readonly reverse: boolean
  private readonly maxStars: number
  private readonly starSize: number
  private readonly step: number
  private readonly showToolTip: boolean
  private readonly rateCallback?: (rating: number, done: () => void) => void
  private readonly onHover?: (currentRating: number, rating: number | null) => void
  private readonly onLeave?: (currentRating: number, rating: number | null) => void
  private readonly isBusyText?: string
  private readonly disableText: string
  private readonly ratingText: string

  private rating: number | null = null
  private currentRating: number = 0
  private myRating: number = 0
  private isRating: boolean = false
  private disabled: boolean = false

  public constructor(options: StarSelectorOptions) {
    if (!options.element) {
      throw new Error("element required")
    }

    if (options.step !== undefined && (options.step <= 0 || options.step > StarSelector.DEFAULT_STEP)) {
      throw new Error("step must be a number between 0 and 1")
    }

    this.element = options.element
    this.reverse = !!options.reverse
    this.maxStars = options.max || StarSelector.DEFAULT_MAX_STARS
    this.starSize = options.starSize || StarSelector.DEFAULT_STAR_SIZE
    this.step = options.step || StarSelector.DEFAULT_STEP
    this.showToolTip = options.showToolTip !== undefined ? !!options.showToolTip : true
    this.rateCallback = options.rateCallback
    this.onHover = options.onHover
    this.onLeave = options.onLeave
    this.isBusyText = options.isBusyText
    this.disableText = options.disableText !== undefined ? options.disableText : StarSelector.RATING_PLACEHOLDER
    this.ratingText = options.ratingText !== undefined ? options.ratingText : StarSelector.RATING_PLACEHOLDER
    this.disabled = !!options.readOnly

    this.element.classList.add("star-rating")
    this.starValueElement = document.createElement("div")
    this.starValueElement.classList.add("star-value")
    if (this.reverse) {
      this.starValueElement.classList.add("rtl")
    }
    this.starValueElement.style.backgroundSize = this.starSize + "px"
    this.element.appendChild(this.starValueElement)
    this.element.style.width = this.starSize * this.maxStars + "px"
    this.element.style.height = this.starSize + "px"
    this.element.style.backgroundSize = this.starSize + "px"

    if (options.rating) {
      this.setRating(options.rating)
    } else {
      const dataRating = this.element.dataset.rating
      if (dataRating) {
        this.setRating(+dataRating)
      }
    }

    if (!this.rating) {
      this.starValueElement.style.width = "0px"
    }

    if (this.disabled) {
      this.disable()
    }

    this.element.addEventListener("mousemove", this.onMouseMove)
    this.element.addEventListener("mouseleave", this.onStarOut)
    this.element.addEventListener("click", this.onStarClick)
    this.element.addEventListener("touchmove", this.handleMove, false)
    this.element.addEventListener("touchstart", this.handleStart, false)
    this.element.addEventListener("touchend", this.handleEnd, false)
    this.element.addEventListener("touchcancel", this.handleCancel, false)
  }

  private onMouseMove = (event: MouseEvent): void => {
    this.onMove(event, false)
  }

  private onMove = (event: MouseEvent | TouchEvent, isTouch: boolean): void => {
    if (this.disabled || this.isRating) {
      return
    }

    const width = this.element.offsetWidth
    const parentOffset = this.element.getBoundingClientRect()
    let xCoordinate: number
    let percent: number

    if (this.reverse) {
      if (isTouch) {
        xCoordinate = (event as TouchEvent).changedTouches[0].pageX - parentOffset.left
      } else {
        xCoordinate = (event as MouseEvent).pageX - window.scrollX - parentOffset.left
      }
      const relativeXRtl = width - xCoordinate
      const valueForDivision = width / StarSelector.PERCENT_MAX
      percent = relativeXRtl / valueForDivision
    } else {
      if (isTouch) {
        xCoordinate = (event as TouchEvent).changedTouches[0].pageX - parentOffset.left
      } else {
        xCoordinate = (event as MouseEvent).offsetX
      }
      percent = (xCoordinate / width) * StarSelector.PERCENT_MAX
    }

    if (percent > StarSelector.PERCENT_MAX) {
      return
    }

    if (this.step === StarSelector.DEFAULT_STEP) {
      this.currentRating = Math.ceil((percent / StarSelector.PERCENT_MAX) * this.maxStars)
    } else {
      const exactRating = (percent / StarSelector.PERCENT_MAX) * this.maxStars
      for (let candidate = 0; ; candidate += this.step) {
        if (candidate >= exactRating) {
          this.currentRating = candidate
          break
        }
      }
    }

    if (this.currentRating > this.maxStars) {
      this.currentRating = this.maxStars
    }

    this.starValueElement.style.width = (this.currentRating / this.maxStars) * StarSelector.PERCENT_MAX + "%"

    if (this.showToolTip) {
      const toolTip = this.ratingText
        .replace("{rating}", String(this.currentRating))
        .replace("{maxRating}", String(this.maxStars))
      this.element.setAttribute("title", toolTip)
    }

    if (this.onHover) {
      this.onHover(this.currentRating, this.rating)
    }
  }

  private onStarOut = (): void => {
    if (!this.rating) {
      this.starValueElement.style.width = "0%"
      this.element.removeAttribute("data-rating")
    } else {
      this.starValueElement.style.width = (this.rating / this.maxStars) * StarSelector.PERCENT_MAX + "%"
      this.element.setAttribute("data-rating", String(this.rating))
    }

    if (this.onLeave) {
      this.onLeave(this.currentRating, this.rating)
    }
  }

  private onStarClick = (): void => {
    if (this.disabled || this.isRating) {
      return
    }

    if (this.rateCallback) {
      this.isRating = true
      this.myRating = this.currentRating

      if (this.isBusyText === undefined) {
        this.element.removeAttribute("title")
      } else {
        this.element.setAttribute("title", this.isBusyText)
      }

      this.element.classList.add("is-busy")
      this.rateCallback(this.myRating, () => {
        if (!this.disabled) {
          this.element.removeAttribute("title")
        }
        this.isRating = false
        this.element.classList.remove("is-busy")
      })
    }
  }

  private handleMove = (event: TouchEvent): void => {
    event.preventDefault()
    this.onMove(event, true)
  }

  private handleStart = (event: TouchEvent): void => {
    event.preventDefault()
    this.onMove(event, true)
  }

  private handleEnd = (event: TouchEvent): void => {
    event.preventDefault()
    this.onMove(event, true)
    this.onStarClick()
  }

  private handleCancel = (event: TouchEvent): void => {
    event.preventDefault()
    this.onStarOut()
  }

  public disable = (): void => {
    this.disabled = true
    this.element.classList.add("disabled")

    if (this.showToolTip && !!this.disableText) {
      const toolTip = this.disableText
        .replace("{rating}", String(this.rating ? this.rating : 0))
        .replace("{maxRating}", String(this.maxStars))
      this.element.setAttribute("title", toolTip)
    } else {
      this.element.removeAttribute("title")
    }
  }

  public enable = (): void => {
    this.disabled = false
    this.element.removeAttribute("title")
    this.element.classList.remove("disabled")
  }

  public setRating = (value: number): void => {
    if (value === undefined || value === null) {
      throw new Error("Value not set.")
    }

    if (typeof value !== "number") {
      throw new Error("Value must be a number.")
    }

    if (value < 0 || value > this.maxStars) {
      throw new Error("Value too high. Please set a rating of " + this.maxStars + " or below.")
    }

    this.rating = value
    this.starValueElement.style.width = (value / this.maxStars) * StarSelector.PERCENT_MAX + "%"
    this.element.setAttribute("data-rating", String(value))
  }

  public getRating = (): number | null => {
    return this.rating
  }

  public clear = (): void => {
    this.rating = null
    this.starValueElement.style.width = "0px"
    this.element.removeAttribute("title")
  }

  public dispose = (): void => {
    this.element.removeEventListener("mousemove", this.onMouseMove)
    this.element.removeEventListener("mouseleave", this.onStarOut)
    this.element.removeEventListener("click", this.onStarClick)
    this.element.removeEventListener("touchmove", this.handleMove, false)
    this.element.removeEventListener("touchstart", this.handleStart, false)
    this.element.removeEventListener("touchend", this.handleEnd, false)
    this.element.removeEventListener("touchcancel", this.handleCancel, false)
  }

  public getElement = (): HTMLElement => {
    return this.element
  }
}
